// src/core/services/resume.service.ts
import api from "@/core/services/axios";
import { withQuery } from "@/core/utils/queryParams";
import type { NewResume, PagedResponse, Resume, ResumeSearchCriteria } from "@/models";
import type { AxiosResponse } from "axios";

export type DownloadType = "doc" | "pdf";

export class ResumeService {
  constructor(private readonly baseRoute = "/resumes") {}

  async fetchResumeById(id: number): Promise<Resume> {
    const res = await api.get<Resume>(`${this.baseRoute}/${id}`);
    return res.data;
  }

  async fetchResumes(criteria: ResumeSearchCriteria): Promise<PagedResponse<Resume>> {
    const url = withQuery(this.baseRoute, criteria as any);
    const res = await api.get<PagedResponse<Resume>>(url);
    return res.data;
  }

  async fetchResumesExport(criteria: ResumeSearchCriteria): Promise<Resume[]> {
    const url = withQuery(`${this.baseRoute}/export`, criteria as any);
    const res = await api.get<Resume[]>(url);
    return res.data;
  }

  async postResume(newResume: NewResume): Promise<Resume> {
    const res = await api.post<Resume>(this.baseRoute, newResume);
    return res.data;
  }

  async deleteResume(id: number): Promise<void> {
    await api.delete(`${this.baseRoute}/${id}`);
  }

  async toggleAIReviewed(id: number, reviewed: boolean): Promise<void> {
    const url = withQuery(`${this.baseRoute}/${id}/ai-reviewed`, { reviewed });
    await api.put(url);
  }

  async toggleFlagged(id: number, flagged: boolean): Promise<void> {
    const url = withQuery(`${this.baseRoute}/${id}/flagged`, { flagged });
    await api.put(url);
  }

  async updateSummary(id: number, summary: string): Promise<void> {
    await api.put(`${this.baseRoute}/${id}/summary`, { summary });
  }

  async downloadResume(resumeId: number, type: DownloadType = "doc"): Promise<AxiosResponse<Blob>> {
    const url = withQuery(`${this.baseRoute}/${resumeId}/download`, { type });
    return api.get(url, { responseType: "blob" });
  }

  async bulkResumeDownload(downloadList: number[]): Promise<AxiosResponse<Blob>> {
    return api.post(`${this.baseRoute}/bulk-download`, downloadList, { responseType: "blob" });
  }

  async downloadPdf(template: string, pageSize: string, resume: any): Promise<AxiosResponse<Blob>> {
    return api.post(
      `${this.baseRoute}/pdf`,
      {
        template: template.toUpperCase(),
        pageSize: pageSize,
        resume: resume,
      },
      { responseType: "blob" }
    );

    // const res = await fetch("/api/resumes/pdf", {
    //   method: "POST",
    //   headers: { "Content-Type": "application/json" },
    //   body: JSON.stringify({
    //     template: template.toUpperCase(), // "MODERN" | "CLASSIC" | "SIDEBAR"
    //     pageSize: pageSize, // "letter" | "a4"
    //     resume: resume.value,
    //   }),
    // });

    // if (!res.ok) throw new Error("PDF generation failed");

    // const blob = await res.blob();
    // const url = URL.createObjectURL(blob);

    // const a = document.createElement("a");
    // a.href = url;
    // a.download = "resume.pdf";
    // a.click();

    // URL.revokeObjectURL(url);
  }
}

export const resumeService = new ResumeService();
